import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Icon } from '@iconify/react';
import { IconButton } from '@mui/material';
import { CLEAR_EDITOR_COMMAND } from 'lexical';
import { $getRoot } from 'lexical';
import { $createParagraphNode } from 'lexical';
import { ImageNode } from '../nodes/ImageNode';

interface SubmitPluginProps {
  onSubmit: (formattedText: string, mentions: string[], cleanMessage: string, files: File[]) => void;
}

export function SubmitPlugin({ onSubmit }: SubmitPluginProps) {
  const [editor] = useLexicalComposerContext();

  const handleSubmit = () => {
    const editorState = editor.getEditorState();
    let formattedText = '';
    let cleanMessage = '';
    const mentions: string[] = [];
    const files: File[] = [];
    const fileNames = new Set<string>();

    editorState.read(() => {
      const rawState = editorState.toJSON();

      formattedText = rawState.root.children.map((node: any) => {
        if (node.type === 'paragraph') {
          const paragraphText = node.children.map((child: any) => {
            if (child.type === 'mention') {
              const id = child.mentionId;
              if (id) {
                mentions.push(id);
              }
              return `[[(@)(${id})${child.mentionName}]]`;
            }

            if (child.type === 'image') {
              let filename = child.filename;
              let baseName = filename.substring(0, filename.lastIndexOf('.'));
              let extension = filename.substring(filename.lastIndexOf('.'));
              let counter = 0;

              while (fileNames.has(filename)) {
                filename = `${baseName}${counter}${extension}`;
                counter++;
              }

              fileNames.add(filename);
              files.push(child.file);
              return `[[(img)${filename}]]`;
            }

            let text = child.text;
            if (child.format) {
              if ((child.format & 1) === 1) {
                text = `**${text}**`;
              }
              if ((child.format & 2) === 2) {
                text = `*${text}*`;
              }
            }
            return text;
          }).join('');

          return paragraphText;
        }
        return '';
      }).filter(Boolean).join('\n\n');

      cleanMessage = rawState.root.children.map((node: any) => {
        if (node.type === 'paragraph') {
          const paragraphText = node.children.map((child: any) => {
            if (child.type === 'mention') {
              return `@${child.mentionName}`;
            }
            if (child.type === 'image') {
              return `[Image]`;
            }
            return child.text;
          }).join('');

          return paragraphText;
        }
        return '';
      }).filter(Boolean).join('\n\n');
    });

    if (formattedText.trim()) {
      onSubmit(formattedText, mentions, cleanMessage, files);

      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        root.append(paragraph);
      });

      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
    }
  };

  return (
    <IconButton
      onClick={handleSubmit}
      sx={{
        color: 'white',
        position: 'absolute',
        right: '8px',
        bottom: '8px',
        zIndex: 2,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <Icon icon="lets-icons:send-duotone" />
    </IconButton>
  );
} 